import React, { lazy } from 'react';
import { AnimatedSection } from '../shared/AnimatedSection';
import { ArtifactCard } from '../shared/ArtifactCard';
import { Sign } from '../../data/signs';
import { motion } from 'framer-motion';
import { startCheckout } from '../../lib/metaPixel';
interface SignProductosProps {
  sign: Sign;
}
const PRODUCTOS_BG = 'https://cdn.shopify.com/s/files/1/0661/3847/2542/files/mm8DHXzU7JhvCgYay4N1kmyc5Q2WN4pg9GNtp0heMg7BSrM3QpCC0xGOYBCXzy5-GOp2Z8fowqxSWnS4ldgA120xXC67zpfx2qL_wjuAepxGwDo6ErItRpus49bMEA7U.jpg?v=1778629864';
// Unique sigil per archive type
const SigilShadow = ({
  color
}: {
  color: string;
}) => <svg width="44" height="44" viewBox="0 0 44 44" fill="none" data-id="element-319">
    <circle cx="22" cy="22" r="20" stroke={color} strokeWidth="0.8" opacity="0.6" data-id="element-320" />
    <path d="M22 6 L30 22 L22 38 L14 22 Z" stroke={color} strokeWidth="1" fill={color} fillOpacity="0.15" data-id="element-321" />
    <circle cx="22" cy="22" r="4" fill={color} data-id="element-322" />
  </svg>;
const SigilCompat = ({
  color
}: {
  color: string;
}) => <svg width="44" height="44" viewBox="0 0 44 44" fill="none" data-id="element-323">
    <circle cx="14" cy="22" r="10" stroke={color} strokeWidth="0.8" opacity="0.7" data-id="element-324" />
    <circle cx="30" cy="22" r="10" stroke={color} strokeWidth="0.8" opacity="0.7" data-id="element-325" />
    <circle cx="22" cy="22" r="2.5" fill={color} data-id="element-326" />
  </svg>;
const SigilRitual = ({
  color
}: {
  color: string;
}) => <svg width="44" height="44" viewBox="0 0 44 44" fill="none" data-id="element-327">
    <circle cx="22" cy="22" r="20" stroke={color} strokeWidth="0.8" opacity="0.6" data-id="element-328" />
    <circle cx="22" cy="22" r="10" fill={color} fillOpacity="0.15" data-id="element-329" />
    <path d="M14 22 A8 8 0 0 1 30 22" stroke={color} strokeWidth="1.2" fill="none" data-id="element-330" />
    <circle cx="14" cy="22" r="1.5" fill={color} data-id="element-331" />
    <circle cx="30" cy="22" r="1.5" fill={color} data-id="element-332" />
  </svg>;
const SigilCamara = ({
  color
}: {
  color: string;
}) => <svg width="44" height="44" viewBox="0 0 44 44" fill="none" data-id="element-333">
    <circle cx="22" cy="22" r="20" stroke={color} strokeWidth="0.8" opacity="0.6" data-id="element-334" />
    {[0, 72, 144, 216, 288].map(a => <line key={a} x1="22" y1="22" x2="22" y2="6" stroke={color} strokeWidth="0.7" opacity="0.7" transform={`rotate(${a} 22 22)`} data-id="element-335" />)}
    <circle cx="22" cy="22" r="3" fill={color} data-id="element-336" />
  </svg>;
const SigilBundle = ({
  color
}: {
  color: string;
}) => <svg width="52" height="52" viewBox="0 0 52 52" fill="none" data-id="element-337">
    <circle cx="26" cy="26" r="24" stroke={color} strokeWidth="0.8" opacity="0.5" data-id="element-338" />
    <circle cx="26" cy="26" r="18" stroke={color} strokeWidth="0.6" strokeDasharray="2 2" opacity="0.6" data-id="element-339" />
    {[0, 60, 120, 180, 240, 300].map(a => <circle key={a} cx="26" cy="6" r="2" fill={color} transform={`rotate(${a} 26 26)`} data-id="element-340" />)}
    <path d="M26 14 L34 26 L26 38 L18 26 Z" stroke={color} strokeWidth="1" fill={color} fillOpacity="0.2" data-id="element-341" />
    <circle cx="26" cy="26" r="3.5" fill={color} data-id="element-342" />
  </svg>;
export function SignProductos({
  sign
}: SignProductosProps) {
  const coreProduct = {
    title: `Núcleo del Arquetipo ${sign.name}`,
    price: 9.99
  };
  const addOns = [{
    id: 'sombrio',
    title: 'Archivo Sombrío',
    description: `Patrones ocultos, heridas simbólicas y reacciones internas que ${sign.name} suele esconder.`,
    microcopy: 'Archivo reservado actualmente dentro del universo expandido.',
    price: '$9',
    sigil: <SigilShadow color={sign.colors.primary} data-id="element-343" />
  }, {
    id: 'compatibilidad',
    title: 'Códice de Compatibilidad',
    description: `Cómo se relaciona ${sign.name} con otras energías: atracción, tensión, choque y magnetismo.`,
    microcopy: 'Próximamente disponible para conexiones avanzadas entre entidades.',
    price: '$10',
    sigil: <SigilCompat color={sign.colors.primary} data-id="element-344" />
  }, {
    id: 'ritual',
    title: 'Ritual del Eclipse',
    description: `Un ritual simbólico de conexión con la energía de ${sign.name} para integrarla de forma consciente.`,
    microcopy: 'Experiencia ritual actualmente en preparación.',
    price: '$8',
    sigil: <SigilRitual color={sign.colors.primary} data-id="element-345" />
  }, {
    id: 'camara',
    title: 'Cámara Astral',
    description: `Experiencia sonora y visual complementaria para sumergirte en la atmósfera del reino de ${sign.name}.`,
    microcopy: 'Experiencia inmersiva en desarrollo dentro del reino.',
    price: '$12',
    sigil: <SigilCamara color={sign.colors.primary} data-id="element-346" />
  }];
  return <section className="relative py-20 px-6 overflow-hidden" data-id="element-347">
      {/* Background image tinted with sign color */}
      <div className="absolute inset-0 z-0" data-id="element-348">
        <img src={PRODUCTOS_BG} alt="" aria-hidden="true" className="w-full h-full object-cover" loading="lazy" data-id="element-349" />
        <div className="absolute inset-0 bg-cosmic-black/80" data-id="element-350" />
        <div className="absolute inset-0" style={{
        backgroundColor: sign.colors.primary,
        opacity: 0.22
      }} data-id="element-351" />
        <div className="absolute inset-0" style={{
        background: `linear-gradient(180deg, #050508 0%, ${sign.colors.primary}15 50%, #050508 100%)`
      }} data-id="element-352" />
      </div>

      <div className="relative z-10 container mx-auto max-w-6xl" data-id="element-353">
        <AnimatedSection data-id="element-354">
          <h2 className="text-3xl md:text-5xl font-serif font-bold text-center text-white mb-4" data-id="element-355">
            Desbloquea Tu Reino
          </h2>
          <p className="text-center mb-12 text-lg italic" style={{
          color: sign.colors.accent
        }} data-id="element-356">
            Comienza tu travesía en el reino de {sign.name}
          </p>
        </AnimatedSection>

        {/* Core Product — only active purchase */}
        <AnimatedSection delay={0.15} data-id="element-357">
          <div className="mb-24 max-w-2xl mx-auto" data-id="element-358">
            <motion.div className="relative border-2 rounded-xl p-8 md:p-10 text-center overflow-hidden" style={{
            borderColor: sign.colors.primary,
            background: `linear-gradient(145deg, rgba(10,10,18,0.95) 0%, ${sign.colors.primary}15 100%)`,
            boxShadow: `0 0 50px ${sign.colors.primary}40, inset 0 0 30px ${sign.colors.primary}10`
          }} data-id="element-359">
              {/* Ambient pulse */}
              <motion.div className="absolute inset-0 rounded-xl pointer-events-none" animate={{
              boxShadow: [`inset 0 0 30px ${sign.colors.primary}15`, `inset 0 0 60px ${sign.colors.primary}30`, `inset 0 0 30px ${sign.colors.primary}15`]
            }} transition={{
              duration: 4,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-360" />
              <div className="relative" data-id="element-361">
                <p className="text-xs tracking-[0.3em] uppercase mb-3 font-serif" style={{
                color: sign.colors.primary
              }} data-id="element-362">
                  Acceso Disponible
                </p>
                <h3 className="text-2xl md:text-3xl font-serif font-bold mb-4" style={{
                color: sign.colors.accent
              }} data-id="element-363">
                  {coreProduct.title}
                </h3>
                <p className="text-gray-300 mb-6 max-w-md mx-auto" data-id="element-364">
                  La experiencia fundamental de tu arquetipo. La primera puerta
                  hacia el reino de {sign.name}.
                </p>
                <p className="text-5xl font-bold mb-8 font-serif" style={{
                color: sign.colors.primary
              }} data-id="element-365">
                  ${coreProduct.price}
                </p>
                <motion.button onClick={() => startCheckout(sign.name, sign.checkoutUrl, coreProduct.price)} className="inline-block px-10 py-4 font-bold rounded-lg transition-colors text-base tracking-wide" style={{
                backgroundColor: sign.colors.primary,
                color: '#050508'
              }} whileHover={{
                scale: 1.03,
                boxShadow: `0 0 40px ${sign.colors.primary}90`
              }} whileTap={{
                scale: 0.97
              }} data-id="element-366">
                  Entrar al Reino de {sign.name}
                </motion.button>
              </div>
            </motion.div>
          </div>
        </AnimatedSection>

        {/* Sealed Archives section */}
        <AnimatedSection delay={0.3} data-id="element-367">
          <div className="text-center mb-12" data-id="element-368">
            <motion.div className="inline-flex items-center gap-3 mb-6" initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} viewport={{
            once: true
          }} data-id="element-369">
              <div className="h-px w-12" style={{
              background: `linear-gradient(90deg, transparent, ${sign.colors.primary})`
            }} data-id="element-370" />
              <span className="text-[11px] tracking-[0.4em] uppercase font-serif" style={{
              color: sign.colors.accent
            }} data-id="element-371">
                Universo Expandido
              </span>
              <div className="h-px w-12" style={{
              background: `linear-gradient(90deg, ${sign.colors.primary}, transparent)`
            }} data-id="element-372" />
            </motion.div>

            <h3 className="text-3xl md:text-4xl font-serif font-bold text-white mb-6" data-id="element-373">
              Archivos Adicionales
            </h3>

            <p className="max-w-2xl mx-auto text-base md:text-lg font-serif italic leading-relaxed" style={{
            color: `${sign.colors.accent}E0`,
            textShadow: `0 0 20px ${sign.colors.primary}40`
          }} data-id="element-374">
              El Reino continúa expandiéndose.
              <br data-id="element-375" />
              Nuevos archivos, rituales y experiencias serán desbloqueados
              dentro del universo LOS 12.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16" data-id="element-376">
            {addOns.map((addOn, index) => <motion.div key={addOn.id} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1 + index * 0.12,
            duration: 0.7
          }} data-id="element-377">
                <ArtifactCard title={addOn.title} description={addOn.description} microcopy={addOn.microcopy} price={addOn.price} signColor={sign.colors.primary} sigil={addOn.sigil} data-id="element-378" />
              </motion.div>)}
          </div>
        </AnimatedSection>

        {/* Sealed Bundle */}
        <AnimatedSection delay={0.5} data-id="element-379">
          <div className="max-w-3xl mx-auto" data-id="element-380">
            <ArtifactCard title={`Acceso Completo al Reino ${sign.name}`} description={`Expansión completa del reino de ${sign.name}: todos los archivos, rituales y experiencias unificadas en una sola travesía.`} microcopy="Expansión completa del reino próximamente disponible." price="$34.99" signColor={sign.colors.accent} isBundle sigil={<SigilBundle color={sign.colors.accent} data-id="element-382" />} data-id="element-381" />
          </div>
        </AnimatedSection>
      </div>
    </section>;
}